import { CanDeactivateFn } from '@angular/router';
import { devLog, ComponentWithBlur } from '@core';

/**
 * Blur Focus Guard
 *
 * **Purpose**: Blur focused element before navigation to prevent aria-hidden warnings.
 *
 * **Usage**: Apply to all routes:
 * ```typescript
 * {
 *   path: 'onboarding',
 *   canDeactivate: [blurOnLeaveGuard],
 *   // ...
 * }
 * ```
 *
 * **Why**: Ionic adds aria-hidden="true" during page transitions. If focus remains
 * on the old page, browsers warn about WCAG violations (focused element hidden from
 * assistive technology).
 *
 * **Related**: WCAG 2.1 Success Criterion 4.1.2 (Name, Role, Value)
 * **Reference**: CONSOLE_WARNINGS_AND_ERRORS_AUDIT.md (P1 Issue #4)
 *
 * **WI-028**: Type-safe guard using ComponentWithBlur from common.types.ts
 */
export const blurOnLeaveGuard: CanDeactivateFn<ComponentWithBlur> = (
  component,
  currentRoute,
  currentState,
  nextState
) => {
  // Blur the currently focused element (use type guard for null check)
  const activeElement: HTMLElement | null = document.activeElement as HTMLElement;
  if (activeElement instanceof HTMLElement && activeElement.blur) {
    activeElement.blur();
    devLog('[BlurOnLeaveGuard] Blurred active element before navigation', {
      from: currentState.url,
      to: nextState?.url
    });
  }

  // Allow navigation to proceed
  return true;
};
